<template>
  <q-card class="full-width" style="max-width: 1200px">
    <q-card-section>
      <div class="row justify-between">
        <div>
          <div>
            {{ selectedUnit.prop.building_name || selectedUnit.prop.street_address }}
          </div>
          <div class="row q-gutter-x-sm items-center">
            <div class="text-h5 text-bold">
              <template v-if="selectedUnit.unit.unit_name">
                Unit {{ selectedUnit.unit.unit_name }}
              </template>
              <template v-else> {{ selectedUnit.unit.floorplan_name || '—' }} </template>
            </div>
            <div>
              <div :class="`hd-${selectedUnit.unit.bed!}-bed-bg hd-badge`">
                <b
                  >{{ selectedUnit.unit.bed }}/{{ selectedUnit.unit.bath
                  }}{{ selectedUnit.unit.partial_bath ? '.5' : '' }}</b
                >
              </div>
            </div>
          </div>
        </div>
        <q-icon size="sm" name="close" class="cursor-pointer" @click="$emit('close')" />
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section class="q-gutter-y-md">
      <div>
        <div class="row q-col-gutter-sm">
          <div
            v-for="c in unitCards"
            :key="c.label"
            class="text-center col"
            style="min-width: 85px"
          >
            <q-card bordered flat class="q-pa-xs ellipsis">
              <div style="font-size: 12px">{{ c.label }}</div>
              <div v-if="c.label === 'Leased Date' && c.value === 'Active'">
                <q-badge outline color="accent">
                  <b>{{ c.value }}</b>
                </q-badge>
              </div>
              <b v-else>{{ c.value }}</b>
            </q-card>
          </div>
        </div>
      </div>
      <template v-if="selectedUnit.unit.amenities?.length || selectedUnit.unit.tags?.length">
        <div class="row q-gutter-x-xs">
          <q-chip
            v-for="a in selectedUnit.unit.amenities"
            :key="`amenity-${a}`"
            square
            style="font-size: 12px"
            :ripple="false"
            :outline="$q.dark.isActive"
          >
            <template v-if="a.length > 50">{{ a.slice(0, 50) }}...</template>
            <template v-else>{{ a }}</template>
            <q-tooltip v-if="a.length > 50">{{ a }}</q-tooltip>
          </q-chip>
          <q-chip
            v-for="t in filterTags(selectedUnit.unit.tags)"
            :key="`tag-${t}`"
            square
            style="font-size: 12px"
            :ripple="false"
            :outline="$q.dark.isActive"
          >
            <template v-if="t.length > 50">{{ getAmenityTitle(t).slice(0, 50) }}...</template>
            <template v-else>{{ getAmenityTitle(t) }}</template>
            <q-tooltip v-if="t.length > 50">{{ getAmenityTitle(t) }}</q-tooltip>
            <q-icon name="auto_awesome" class="q-ml-xs" color="primary">
              <q-tooltip>
                AI-Detected Unit Amenity: detected from floorplan images, floorplan names, and
                advertised amenities.
              </q-tooltip>
            </q-icon>
          </q-chip>
        </div>
      </template>
      <q-markup-table v-if="leaseTerms.length" flat bordered dense>
        <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <tr>
            <th v-for="plan in leaseTerms" :key="plan[0]" scope="col" class="text-center">
              {{ plan[0] }} mo.
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              v-for="plan in leaseTerms"
              :key="plan[0]"
              class="text-center"
              :class="{ 'bg-deep-purple-light text-bold': plan[1] === selectedUnit.unit.price }"
            >
              {{ formatMoney(plan[1]) }}
            </td>
          </tr>
        </tbody>
      </q-markup-table>
      <q-card v-if="priceHistory.length" flat bordered>
        <div class="q-px-md">
          <property-compare-history-chart
            class="q-mb-md"
            label="Asking Rent"
            :history="priceHistory"
            :compute-aggregates="false"
            span-gaps
          />
        </div>
        <q-separator />
        <concessions-card
          v-if="selectedUnit"
          :property="selectedUnit.prop"
          :unit="selectedUnit.unit"
          :period="selectedUnit.period"
        />
      </q-card>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'

import {
  type PropertyDetailsResponse,
  type Availability,
  type AvailabilityPeriod,
  type Price,
  getAmenityTitle
} from 'common'
import { availabilityDate, daysOnMarket, numApplications, numDenials } from 'common'
import { filterTags, formatMoney, formatNumber, unitTypeColor } from '@/utils'
import PropertyCompareHistoryChart, {
  type Value,
  type Line
} from './PropertyCompareHistoryChart.vue'
import ConcessionsCard from './ConcessionsCard.vue'
import { format, parseISO, differenceInDays, eachDayOfInterval } from 'date-fns'

export default {
  name: 'UnitLevelCard',
  components: {
    PropertyCompareHistoryChart,
    ConcessionsCard
  },
  props: {
    selectedUnit: {
      type: Object as PropType<{
        unit: Availability
        prop: PropertyDetailsResponse
        period: AvailabilityPeriod
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    }
  },
  emits: ['close'],
  data() {
    return {
      format,
      parseISO,
      formatMoney,
      formatNumber,
      availabilityDate,
      priceHistory: [] as Value[]
    }
  },
  computed: {
    unavailableDays() {
      const days = new Set<string>()
      for (const period of this.selectedUnit.period.unavailable_periods || []) {
        for (const day of eachDayOfInterval({
          start: parseISO(period.from_date),
          end: parseISO(period.to_date)
        })) {
          days.add(format(day, 'yyyy-MM-dd'))
        }
      }
      return days
    },
    unitCards() {
      const response: { label: string; value: string }[] = []
      const { unit, period } = this.selectedUnit
      if (unit.floorplan_name) {
        response.push({ label: 'Floorplan', value: unit.floorplan_name })
      }
      if (unit.floor) {
        response.push({ label: 'Floor', value: unit.floor.toString() })
      }
      if (unit.sqft) {
        response.push({ label: 'SF', value: formatNumber(unit.sqft) })
      } else if (unit.min_sqft && unit.max_sqft) {
        response.push({
          label: 'SF',
          value: `${formatNumber(unit.min_sqft)} - ${formatNumber(unit.max_sqft)}`
        })
      }
      if (period.deposit) {
        response.push({ label: 'Deposit', value: formatMoney(period.deposit) })
      }
      if (period.enter_market) {
        response.push({
          label: 'On Mkt Date',
          value: format(parseISO(period.enter_market), 'M/d/yyyy')
        })
      }
      if (period.availability) {
        response.push({
          label: 'Available On',
          value: format(parseISO(period.availability), 'M/d/yyyy')
        })
      }
      if (period.exit_market) {
        response.push({
          label: 'Leased Date',
          value: format(parseISO(period.exit_market), 'M/d/yyyy')
        })
      } else {
        response.push({ label: 'Leased Date', value: 'Active' })
      }
      const days = daysOnMarket(period, new Date())
      if (days) {
        response.push({
          label: 'Days on Mkt',
          value: days.toString()
        })
      }
      if (period.availability) {
        const daysVacant = differenceInDays(period.exit_market || new Date(), period.availability)
        if (daysVacant > 0) {
          response.push({
            label: 'Vacant Days',
            value: daysVacant.toString()
          })
        }
      }
      const numApps = numApplications(period)
      response.push({
        label: '# Applications',
        value: formatNumber(numApps)
      })
      const numDeny = numDenials(period)
      response.push({
        label: '# Cancels',
        value: formatNumber(numDeny)
      })

      return response
    },
    leaseTerms() {
      return (
        this.selectedUnit.unit.history.findLast(
          (_) => _.period_id === this.selectedUnit.period.period_id
        )?.price_plans || []
      )
    }
  },
  mounted() {
    const unitHistoryData = this.expandedUnitHistory[this.selectedUnit.unit.id]
    if (!unitHistoryData || !Array.isArray(unitHistoryData)) {
      this.priceHistory = []
      return
    }

    const unitHistory = unitHistoryData.filter(
      (_) => _.history.period_id === this.selectedUnit.period.period_id
    )
    const priceHistory = [
      { label: 'Asking Rent', history: [], color: 'rgba(211, 47, 47, 1)' } as Line,
      { label: 'Effective Rent', history: [], color: 'rgba(99, 101, 241, 1)' } as Line,
      { label: 'Min Asking Rent', history: [], color: 'rgba(0, 151, 167, 1)' } as Line,
      { label: 'Max Asking Rent', history: [], color: 'rgba(251, 192, 45, 1)' } as Line,
      { label: 'Min Effective Rent', history: [], color: 'rgba(25, 118, 210, 1)' } as Line,
      { label: 'Max Effective Rent', history: [], color: 'rgba(56, 142, 60, 1)' } as Line
    ]
    for (const record of unitHistory) {
      const isAvailable = !this.unavailableDays.has(record.created_on)
      if (!isAvailable) {
        continue
      }
      if (record.history.effective_price) {
        priceHistory[1].history.push({
          date: record.created_on,
          value: record.history.effective_price
        })
      } else if (record.history.min_effective_price && record.history.max_effective_price) {
        priceHistory[4].history.push({
          date: record.created_on,
          value: record.history.min_effective_price
        })
        priceHistory[5].history.push({
          date: record.created_on,
          value: record.history.max_effective_price
        })
      }
      if (record.history.price) {
        priceHistory[0].history.push({
          date: record.created_on,
          value: record.history.price
        })
      } else if (record.history.min_price && record.history.max_price) {
        priceHistory[2].history.push({
          date: record.created_on,
          value: record.history.min_price
        })
        priceHistory[3].history.push({
          date: record.created_on,
          value: record.history.max_price
        })
      }
    }
    this.priceHistory = priceHistory.filter((_) => _.history.length > 0)
  },
  methods: {
    getAmenityTitle,
    filterTags,
    daysOnMarket,
    unitTypeColor
  }
}
</script>
